import React from 'react';
import { Alert, CircularProgress, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ResponsiveDialog from '../common/ResponsiveDialog';
import SupplierEditor from './SupplierEditor';
import supplierApi, { supplierError } from '../../services/supplierApi';
import type { SupplierConfig } from '../../types/suppliers';

/**
 * Define a supplier from a petty cash line: the VAT number on the receipt is not in SAP. The same form and
 * checks as Petty Cash > Suppliers; the new vendor's code comes back to the line through onCreated.
 * The caller needs the 'suppliers' module (the server refuses others).
 */

export interface SupplierQuickCreateDialogProps {
  open: boolean;
  companyId: number;
  initialVat?: string;
  initialName?: string;
  onClose: () => void;
  onCreated: (v: { cardCode: string; cardName: string; vat: string }) => void;
}

const SupplierQuickCreateDialog: React.FC<SupplierQuickCreateDialogProps> = ({ open, companyId, initialVat, initialName, onClose, onCreated }) => {
  const [config, setConfig] = React.useState<SupplierConfig | null>(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open || !companyId) return undefined;
    let cancelled = false;
    setError('');
    supplierApi.config(companyId)
      .then((c) => { if (!cancelled) setConfig(c); })
      .catch((e) => { if (!cancelled) setError(supplierError(e, 'The supplier lists could not be read from SAP')); });
    return () => { cancelled = true; };
  }, [open, companyId]);

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="supplier-quick-create-title">
      <DialogTitle id="supplier-quick-create-title" sx={{ pr: 6 }}>
        Define this supplier
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!config && !error ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Reading the supplier lists from SAP…</Typography>
          </Stack>
        ) : null}
        {config && open ? (
          <SupplierEditor
            key={`${companyId}-${initialVat || ''}-${initialName || ''}`}
            companyId={companyId}
            config={config}
            initial={{ vat: initialVat || '', name: initialName || '' }}
            source="Claim"
            layout="dialog"
            onCancel={onClose}
            onCreated={(created) => onCreated({ cardCode: created.cardCode, cardName: created.cardName, vat: created.vat })}
          />
        ) : null}
      </DialogContent>
    </ResponsiveDialog>
  );
};

export default SupplierQuickCreateDialog;
